import { Bookmark, Save, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Saved-search drawer. Users can name the current filter set + save it, or
 * apply / delete any previously saved search. Rendered next to the header
 * pill on the top-right.
 */
export default function NearbySavedDrawer({
  saved,
  savedName,
  onSavedNameChange,
  onSave,
  onApply,
  onDelete,
  onClose,
}) {
  return (
    <div
      className="pointer-events-auto absolute top-16 right-4 z-[1250] w-80 max-w-[92vw] bg-zinc-950/95 border border-white/10 rounded-2xl backdrop-blur-md shadow-2xl p-4"
      data-testid="nearby-saved-drawer"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="font-display text-lg text-white flex items-center gap-2">
          <Bookmark size={16} className="text-rose-500" /> Saved searches
        </div>
        <button onClick={onClose} className="text-white/60 hover:text-white">
          <X size={16} />
        </button>
      </div>
      <div className="flex items-center gap-2 mb-3">
        <Input
          value={savedName}
          onChange={(e) => onSavedNameChange(e.target.value.slice(0, 60))}
          placeholder="Name this search…"
          className="bg-black/40 border-white/10 rounded-full text-sm"
          data-testid="nearby-saved-name"
          onKeyDown={(e) => e.key === "Enter" && onSave()}
        />
        <Button
          onClick={onSave}
          data-testid="nearby-saved-save"
          className="rounded-full btn-crimson px-4"
        >
          <Save size={14} className="mr-1" /> Save
        </Button>
      </div>
      {saved.length === 0 ? (
        <div className="text-xs text-zinc-500 text-center py-4">
          No saved searches yet. Configure your radius + filters, then hit save.
        </div>
      ) : (
        <div className="space-y-1.5 max-h-72 overflow-y-auto">
          {saved.map((f) => (
            <div
              key={f.id}
              data-testid={`nearby-saved-row-${f.id}`}
              className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl p-2.5 hover:bg-white/10 transition"
            >
              <button
                onClick={() => onApply(f)}
                className="flex-1 text-left min-w-0"
                data-testid={`nearby-saved-apply-${f.id}`}
              >
                <div className="text-sm text-white font-semibold truncate">{f.name}</div>
                <div className="text-[11px] text-zinc-500 truncate">
                  {f.radius_mi ? `${f.radius_mi} mi` : "everywhere"}
                  {f.online_only && " · online"}
                  {f.verified_only && " · verified"}
                  {f.paying_only && " · paying"}
                  {Array.isArray(f.desires) && f.desires.length > 0 && (
                    <span className="text-rose-400"> · ✨ {f.desires.length}</span>
                  )}
                </div>
              </button>
              <button
                onClick={() => onDelete(f.id)}
                data-testid={`nearby-saved-delete-${f.id}`}
                className="text-zinc-500 hover:text-rose-500 p-1.5 rounded-full"
                title="Delete"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
